// Marketing Practice Library — visitor-facing grid of every practice.
// Route: /practices. Each tile links to /practices/:slug for a full detail page.

import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { AppMenu } from "../components/AppMenu";
import { useAuth } from "../App";
import { useBilling } from "../contexts/BillingContext";
import { ArrowRight, Clock, Headphones, Lock } from "lucide-react";
import { PRACTICE_CATALOG } from "../lib/practiceCatalog";
import { LIBRARY_PRACTICES } from "../lib/libraryPractices";
import { isLockedForUser } from "../lib/trialAccess";
import { showGoogleSignIn } from "../lib/platform";
import useSeo from "../hooks/useSeo";

const GROUP_ORDER = [
  "Emotional grounding",
  "Calm body",
  "Sleep health",
  "Growth & confidence",
  "Repair & transformation",
];

const PracticesPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  useSeo({
    title: "Practice Library — Bilateral Calm",
    description:
      "Browse all 20 guided bilateral practices and the Daily Practices audio library — from anxiety and overwhelm to stage fright, sleep, grief, and nightmare reset.",
    path: "/practices",
  });

  // Read billing status from the shared BillingContext (fetched once at
  // sign-in) so this page doesn't add a redundant /api/billing/status
  // request on every visit. Signed-out visitors get billingStatus=null
  // which the paywall logic treats as unrestricted-in-UI.
  const { billing } = useBilling();
  const billingStatus = billing?.status || null;

  const grouped = GROUP_ORDER.map((g) => ({
    group: g,
    items: PRACTICE_CATALOG.filter((p) => p.group === g),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen relative" data-testid="practices-page">
      <div
        className="fixed inset-0 -z-10 bg-cover bg-bottom"
        style={{
          backgroundImage: "url('/images/learn-more-wallpaper.webp')",
          filter: "saturate(1.18) brightness(1.06)",
        }}
        aria-hidden
      />
      <div
        className="fixed inset-0 -z-10"
        style={{ backgroundColor: "rgba(251,248,241,0.85)" }}
        aria-hidden
      />

      {/* Header */}
      <header className="bg-[#FBF8F1]/80 backdrop-blur-md border-b border-slate-100/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center"
            data-testid="practices-home-btn"
          >
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-28 sm:h-32 w-auto" />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            {!user && (
              <>
                <button
                  onClick={() => navigate("/practices")}
                  className="hidden sm:inline-flex text-sm text-[#1B2349] font-semibold px-3 py-2 rounded-full"
                  data-testid="practices-nav-practices-btn"
                >
                  Practice Library
                </button>
                <button
                  onClick={() => navigate("/pricing")}
                  className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
                  data-testid="practices-nav-plans-btn"
                >
                  Plans
                </button>
                <button
                  onClick={() => navigate("/about")}
                  className="hidden md:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
                  data-testid="practices-nav-about-btn"
                >
                  About
                </button>
                <button
                  onClick={() => navigate("/faq")}
                  className="hidden sm:inline-flex text-sm text-[#4A5568] hover:text-[#1B2349] font-medium px-3 py-2 rounded-full"
                  data-testid="practices-nav-faq-btn"
                >
                  FAQ
                </button>
                <Button data-testid="practices-signin-btn" onClick={() => navigate("/sign-in")} className="btn-primary">
                  Sign in
                </Button>
              </>
            )}
            {user && <AppMenu />}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        <section className="max-w-3xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-3">
            Full library
          </p>
          <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight mb-5 leading-[1.05]">
            20 guided practices.
            <br />
            Plus a daily audio library.
          </h1>
          <p className="text-lg text-[#1B2349]/80 leading-relaxed">
            Every practice in Bilateral Calm uses the same gentle left-right audio your brain
            already knows from REM sleep. Browse the full library — pick the one that matches
            today's moment, or start the 5-day free trial to unlock them all.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            {user ? (
              <Button
                onClick={() => navigate("/dashboard")}
                className="btn-primary"
                data-testid="practices-go-dashboard-btn"
              >
                Open your dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="btn-primary"
                  data-testid="practices-cta-start-trial-btn"
                >
                  Start the 5-day free trial
                </Button>
                {showGoogleSignIn() && (
                  <Button
                    onClick={login}
                    variant="outline"
                    className="rounded-full border-[#1B2349] text-[#1B2349] hover:bg-[#1B2349] hover:text-white"
                    data-testid="practices-cta-google-btn"
                  >
                    Sign in with Google
                  </Button>
                )}
              </>
            )}
          </div>
        </section>

        {grouped.map(({ group, items }) => (
          <section key={group} className="mb-16">
            <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] mb-2">
              {group}
            </h2>
            <div className="h-px bg-[#1B2349]/10 mb-6" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
              {items.map((item) => {
                const isLocked = user && isLockedForUser(item.slug, billingStatus);
                return (
                <button
                  key={item.slug}
                  onClick={() => navigate(`/practices/${item.slug}`)}
                  className="group/p relative text-left w-full rounded-2xl overflow-hidden bg-white/65 backdrop-blur-md border border-white/40 hover:bg-white/85 hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgba(27,35,73,0.06)] hover:shadow-[0_10px_30px_rgba(27,35,73,0.12)] focus:outline-none focus:ring-2 focus:ring-[#3090A8]/50"
                  data-testid={`practice-card-${item.slug}`}
                >
                  <div
                    className="aspect-square w-full bg-cover bg-center"
                    style={{ backgroundImage: `url('${item.image}')` }}
                  />
                  {isLocked && (
                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                      <Lock className="w-3.5 h-3.5 text-[#7A5C2E]" />
                    </div>
                  )}
                  <div className="p-3 sm:p-4">
                    <h3 className="font-outfit font-medium text-sm sm:text-base text-[#1B2349] leading-tight">{item.title}</h3>
                    <p className="text-[#4A5568] text-xs sm:text-sm mt-1 leading-relaxed">{item.hook}</p>
                    <p className="text-[11px] font-medium mt-2 inline-flex items-center gap-1 text-[#3090A8] opacity-0 group-hover/p:opacity-100 transition-opacity">
                      {isLocked ? "Unlock with a plan" : "Read more"} <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                </button>
                );
              })}
            </div>
          </section>
        ))}

        {/* Daily Practices — bilateral audio sessions (meditations + ambient
            soundscapes). Tiles link signed-in users to the in-app library
            player; signed-out visitors go to /pricing so they can sign up. */}
        <section className="mb-16">
          <h2 className="font-outfit font-semibold text-xl sm:text-2xl text-[#1B2349] mb-2 flex items-center gap-2">
            <Headphones className="w-5 h-5 text-[#3090A8]" />
            Daily Practices — audio library
          </h2>
          <p className="text-sm text-[#4A5568] mb-4">
            Short, calming audio sessions for any moment. Pair them with the guided practices above or use them on their own.
          </p>
          <div className="h-px bg-[#1B2349]/10 mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {LIBRARY_PRACTICES.map((item) => {
              const isLocked = user && isLockedForUser(item.id, billingStatus);
              return (
              <button
                key={item.id}
                onClick={() => {
                  if (!user) return navigate("/pricing");
                  if (isLocked) return navigate(`/library/${item.id}`); // lands on paywall view
                  return navigate(`/library/${item.id}`);
                }}
                className="group/p relative text-left w-full rounded-2xl overflow-hidden bg-white/65 backdrop-blur-md border border-white/40 hover:bg-white/85 hover:-translate-y-1 transition-all duration-300 shadow-[0_4px_20px_rgba(27,35,73,0.06)] hover:shadow-[0_10px_30px_rgba(27,35,73,0.12)] focus:outline-none focus:ring-2 focus:ring-[#3090A8]/50"
                data-testid={`library-card-${item.id}`}
              >
                <div
                  className="aspect-square w-full bg-cover bg-center"
                  style={{
                    backgroundImage: `url('${item.image}')`,
                    backgroundPosition: item.imagePosition || "center",
                  }}
                />
                {isLocked && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                    <Lock className="w-3.5 h-3.5 text-[#7A5C2E]" />
                  </div>
                )}
                <div className="p-3 sm:p-4">
                  <h3 className="font-outfit font-medium text-sm sm:text-base text-[#1B2349] leading-tight">{item.title}</h3>
                  <div className="flex items-center gap-2 text-[11px] text-[#718096] mt-1.5">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {item.duration}
                    </span>
                    <span className="opacity-50">·</span>
                    <span>{item.category}</span>
                  </div>
                  <p className="text-[11px] font-medium mt-2 inline-flex items-center gap-1 text-[#3090A8] opacity-0 group-hover/p:opacity-100 transition-opacity">
                    {!user ? "Sign up to listen" : isLocked ? "Unlock with a plan" : "Listen now"} <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </button>
              );
            })}
          </div>
        </section>

        <section className="bg-white/65 backdrop-blur-md border border-white/40 rounded-3xl p-10 text-center mt-16 shadow-[0_4px_20px_rgba(27,35,73,0.06)]">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mb-3">
            Try the full library, free for 5 days.
          </h2>
          <p className="text-[#4A5568] max-w-2xl mx-auto mb-6">
            All 20 guided practices and the Daily Practices audio library unlock the moment you sign up. Cancel anytime from your account — no questions asked.
          </p>
          <Button
            onClick={() => navigate(user ? "/dashboard" : "/pricing")}
            className="btn-primary text-base px-8 py-3"
            data-testid="practices-bottom-cta-btn"
          >
            {user ? "Open your dashboard" : "Start sign up"}
          </Button>
        </section>
      </main>

      <footer className="py-8 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img src="/logo-bilateral-calm.png" alt="Bilateral Calm" className="h-20 sm:h-24 w-auto" />
          </div>
          <p className="text-[#718096] text-sm">
            © 2026 Mood and Sleep Clinic LLC · Bilateral Calm™ · <a href="mailto:mscwellness@protonmail.com" className="underline underline-offset-2 hover:text-[#1B2349]">mscwellness@protonmail.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PracticesPage;
